/* StyledSelect — single-value styled dropdown.

   Matches the visual aesthetic of the MultiSelect already used in Feed.jsx
   (white pill, soft border, ChevronDown caret, navy text) so every filter
   across the app shares one look.  No external dep — but the option list
   is rendered through a portal into document.body, positioned against the
   trigger's own bounding box. This keeps it from being clipped by any
   ancestor with `overflow` set (e.g. the sticky filter sidebar), on both
   desktop and mobile.
*/
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";

export default function StyledSelect({
  options,            // [{ v: string, label: string, icon?: ReactNode }]
  value,              // current selected v
  onChange,           // (newV) => void
  testid,
  placeholder = "Select",
  className = "",
  size = "md",        // "md" | "sm"
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const current = options.find((o) => o.v === value);
  const padY = size === "sm" ? "py-1.5" : "py-2";

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({
      top: r.bottom + window.scrollY + 4,
      left: r.left + window.scrollX,
      width: r.width,
    });
  };

  // Recompute position right before paint whenever the menu opens, so it
  // never flashes in the wrong spot on first render.
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  // Keep the menu glued to the trigger on scroll/resize. `true` (capture
  // phase) means this also fires when the *sidebar itself* scrolls, not
  // just the window — that's the scroll context that was causing the
  // clipping in the first place.
  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Close on outside click — now needs to check the portaled list too,
  // since it's no longer a DOM descendant of the trigger's wrapper.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        listRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className={`relative ${className}`} data-testid={testid}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid={testid ? `${testid}-trigger` : undefined}
        className={`w-full flex items-center justify-between px-3 ${padY} rounded-md border border-[#E2E8F0] bg-white text-sm transition-colors hover:border-[#CBD5E1] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]`}
      >
        <span className="inline-flex items-center gap-2 min-w-0 truncate">
          {current?.icon}
          <span className={current ? "text-[#0F1F3D]" : "text-[#94A3B8]"}>
            {current ? current.label : placeholder}
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[#64748B] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open &&
        createPortal(
          <ul
            ref={listRef}
            role="listbox"
            style={{
              position: "absolute",
              top: coords.top,
              left: coords.left,
              width: coords.width,
              zIndex: 9999,
            }}
            className="bg-white border border-[#E2E8F0] rounded-md shadow-lg p-1 max-h-64 overflow-auto"
            data-testid={testid ? `${testid}-list` : undefined}
          >
            {options.map((o) => {
              const selected = o.v === value;
              return (
                <li
                  key={o.v}
                  role="option"
                  aria-selected={selected}
                  data-testid={testid ? `${testid}-opt-${o.v}` : undefined}
                  onClick={() => { onChange(o.v); setOpen(false); }}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer ${
                    selected
                      ? "bg-[#F1F5F9] text-[#0F1F3D] font-medium"
                      : "text-[#0F1F3D] hover:bg-[#F8FAFC]"
                  }`}
                >
                  {o.icon}
                  <span className="flex-1 truncate">{o.label}</span>
                  {selected && <Check className="h-3.5 w-3.5 text-[#0F1F3D]" />}
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
}